// Domain types for the payroll experience. These mirror the payroll-service
// backend (services/golang/payroll-service) so that swapping the mock data layer
// in `api/payrollApi.ts` for live gateway calls is a mechanical change — the
// screens consume these shapes, not the wire format.

export type CountryCode = 'TZ' | 'KE' | 'RW';
export type Currency = 'TZS' | 'KES' | 'RWF';

/** Run lifecycle, matching payrollrun.State on the backend. */
// 'cancelled' is a terminal state alongside the lifecycle, NOT a step in it —
// see RUN_STATE_ORDER, which deliberately leaves it out.
export type RunState = 'draft' | 'validated' | 'approved' | 'locked' | 'paid' | 'cancelled';

/** Per-employee line status within a run (payroll_run_employees.status).
 *  Only a 'calculated' line has money; 'error' could not be calculated (e.g. a
 *  missing statutory ID), 'excluded' is deliberately not paid by this run
 *  (outside the employment window, payroll disabled…), 'pending' has not been
 *  calculated yet. The last three always carry the reason, never figures. */
export type LineStatus = 'calculated' | 'error' | 'excluded' | 'pending';

/** Pay-component classification, for grouping a payslip/line breakdown. */
export type ComponentKind = 'earning' | 'pre_tax' | 'tax' | 'post_tax' | 'employer';

export interface PayComponent {
  code: string; // BASIC, PAYE, NSSF_EE, HOUSING_EE, …
  label: string;
  amount: number; // signed: deductions negative
  kind: ComponentKind;
  statutory?: boolean;
}

export interface RunTotals {
  gross: number;
  taxableIncome: number;
  employeeDeductions: number;
  employerContributions: number;
  net: number;
  employerCost: number;
}

export interface PayrollRun {
  id: string;
  legalEntityId: string;
  legalEntityName: string;
  country: CountryCode;
  currency: Currency;
  frequency: 'monthly' | 'weekly';
  periodStart: string; // YYYY-MM-DD
  periodEnd: string;
  periodLabel: string; // e.g. "November 2024"
  state: RunState;
  /** Optional human name ("May correction", "Bonus run"). NOT part of the run's
   *  identity: uniqueness stays on entity + period + frequency, so naming two
   *  runs differently can never create a second run for one period. */
  label?: string;
  /** Set only when state === 'cancelled'. A list that says "cancelled" without
   *  saying why sends the reader into the audit log for what the row knows. */
  cancelledAt?: string;
  cancelReason?: string;
  cancelNote?: string;
  /** Set only when the run reached 'paid' by a human recording a settlement made
   *  outside KaziPeople (bank file, transfer, cash) rather than by a disbursement
   *  the platform executed. Absent on in-platform payments, so "paid" can say
   *  which of the two it was instead of covering both with one word. */
  paidExternallyAt?: string;
  externalPaymentMethod?: RunExternalPaymentMethod;
  externalPaymentReference?: string;
  externalPaymentNote?: string;
  /** Which approval policy the run was SUBMITTED under (its Phase 4 snapshot). Absent until
   *  the run is submitted, and for runs submitted before per-company policies existed. */
  policyId?: string;
  policyVersion?: number;
  policySource?: 'builtin' | 'tenant' | 'entity';
  totals: RunTotals;
  employeeCount: number;
  calculated: number;
  errored: number;
  createdAt: string;
}

export interface EmployeeLine {
  employeeId: string;
  fullName: string;
  department?: string;
  departmentId?: string;
  status: LineStatus;
  errorReason?: string;
  gross: number;
  taxableIncome: number;
  employeeDeductions: number;
  employerContributions: number;
  net: number;
  components: PayComponent[];
}

/** 'group' = the optional Group Finance stage (Phase 4): only an approver with access to
 *  every company may sign it. */
export type ApprovalLevel = 'hr' | 'finance' | 'group' | 'final';
export type ApprovalDecision = 'pending' | 'approved' | 'rejected';

export interface Approval {
  level: ApprovalLevel;
  decision: ApprovalDecision;
  /** The policy's own label for the level, when it set one. */
  label?: string;
  /** 'group' when this stage needs a Group Finance approver (access to every company). */
  scope?: 'group';
  actorName?: string;
  note?: string;
  decidedAt?: string;
}

export type ExportType =
  | 'payroll_summary'
  | 'tra_statutory'
  | 'nssf_statutory'
  | 'wcf_statutory'
  | 'sdl_statutory'
  | 'heslb_statutory'
  | 'bank_payment'
  | 'bank_review'
  | 'gl_journal';

export type ExportFormat = 'csv' | 'xlsx' | 'pdf' | 'json';
export type ExportStatus =
  | 'requested'
  | 'generating'
  | 'generated'
  | 'failed'
  | 'approved'
  | 'submitted';

export interface ExportError {
  /** 'error' blocks generation; 'warning' is non-blocking (e.g. an employee
   *  excluded from a WCF return for a missing Contract Type — the file still
   *  generated). Absent on legacy records → treat as 'error'. */
  severity?: 'error' | 'warning';
  code: string;
  field?: string;
  message: string;
  employeeId?: string;
  /** Present on per-employee errors so the UI can name who fails. */
  employeeName?: string;
}

export type ExportCategory = 'report' | 'integration';

export interface PayrollExport {
  id: string;
  runId: string;
  type: ExportType;
  format: ExportFormat;
  status: ExportStatus;
  version: number;
  fileName?: string;
  checksum?: string;
  generatedAt?: string;
  /** Reports (human, XLSX/PDF) vs Integrations (external submission, official format + checksum). */
  category?: ExportCategory;
  /** The official submission format for this type (csv for bank, etc.). */
  officialFormat?: ExportFormat;
  /** The party an integration is submitted to (e.g. "Bank", "TRA"). */
  submissionTarget?: string;
  checksumAlgorithm?: string;
  generatedBy?: string;
  totals?: Record<string, string>;
  metadata?: Record<string, unknown>;
  /** Why a failed export failed (surfaced from the server). */
  errorCount?: number;
  errors?: ExportError[];
  /** OUTDATED: the run was reopened/recalculated after this file was generated. */
  invalidatedAt?: string;
  invalidatedReason?: string;
  /** Destination-format revision that produced the file (e.g. "2026.1"). */
  templateVersion?: string;
  /** Originating bank for bank files (CRDB / NMB). */
  bankCode?: string;
}

/** One submission target for a run (statutory-submissions program). */
export interface SubmissionDestination {
  exportType: ExportType;
  target: string;
  filingType: FilingType;
  bankCode?: string;
  configured: boolean;
  templateName?: string;
  templateVersion?: string;
  defaultFormat?: ExportFormat;
  deliveryMethod: string; // manual_download (SFTP/API not yet enabled)
}

export type DisbursementChannel = 'bank' | 'mobile_money';
export type DisbursementItemStatus =
  | 'pending'
  | 'submitted'
  // Mirrored from paygate: sent, outcome not yet known. In flight — never
  // failed, never retried; it settles when the provider answers.
  | 'unknown'
  | 'paid'
  | 'failed'
  | 'reversed'
  | 'reconciliation_required';
export type DisbursementBatchStatus =
  | 'prepared'
  | 'submitting' // claimed for release, connector hand-off in progress (async worker)
  | 'submitted'
  | 'completed'
  | 'failed';

/** What a batch pays for. Absent on older batches (net pay implied). */
export type DisbursementPurpose = 'net_pay' | 'loan_advance' | 'terminal_dues';

/** Failure classification (audit §25) — controls whether a Retry is offered. */
export type RetryClass =
  | 'safe_to_retry'
  | 'retry_with_status_check'
  | 'do_not_retry'
  | 'manual_review_required';

export interface DisbursementItem {
  id: string;
  employeeName: string;
  channel: DisbursementChannel;
  provider: string; // NMB/CRDB… or mpesa/airtel…
  accountMasked: string; // ••••1234
  amount: number;
  /** Present when the item renders outside its batch (reconciliation queue). */
  currency?: Currency;
  status: DisbursementItemStatus;
  /** Which share of a salary split this item pays (absent = unsplit). */
  allocationSeq?: number;
  connectorRef?: string;
  /** How many times this payment was sent to the connector. */
  attempts: number;
  submittedAt?: string;
  /** Only on failed items — whether/how a retry is allowed. */
  retryClass?: RetryClass;
  // Manual reconciliation trail (items resolved by a human against the provider).
  reconciliationNote?: string;
  reconciledBy?: string | null;
  reconciledAt?: string | null;
}

export interface DisbursementBatch {
  id: string;
  runId: string;
  connector: string; // mock | azampay | clickpesa
  status: DisbursementBatchStatus;
  purpose?: DisbursementPurpose;
  /** Set when purpose === 'terminal_dues'. */
  settlementId?: string;
  /** Set when purpose === 'loan_advance'. */
  loanId?: string;
  currency: Currency;
  totalAmount: number;
  itemCount: number;
  // Integrity summary (audit §24): settled-vs-expected at a glance.
  statusCounts: Record<string, number>;
  paidTotal: number;
  fullyReconciled: boolean;
  preparedBy?: string;
  submittedBy?: string;
  preparedAt: string;
  submittedAt?: string;
  completedAt?: string;
  items: DisbursementItem[];
}

// Loans/advances moved to credit-service — see src/features/credit. The legacy
// payroll loan types + client methods were removed with that cutover.

export type PayslipStatus = 'pending' | 'generated';

export interface Payslip {
  id: string;
  employeeName: string;
  periodLabel: string;
  payDate: string;
  country: CountryCode;
  currency: Currency;
  gross: number;
  net: number;
  status: PayslipStatus;
  components: PayComponent[];
}

/** Lightweight option for pickers. */
export interface LegalEntityLite {
  id: string;
  name: string;
  country: CountryCode;
  currency: Currency;
}

export interface EmployeeLite {
  id: string;
  name: string;
}

export interface NewRunInput {
  legalEntityId: string;
  periodStart: string; // YYYY-MM-DD
  periodEnd: string;
  /** Optional name, set at creation so the second run for a month is
   *  distinguishable the moment it appears rather than after someone renames it. */
  label?: string;
}

/** The answer to "create a run". Create is idempotent on (entity, period,
 *  frequency): asking for a period that already has a run returns THAT run (HTTP
 *  200) instead of a new one (201). `created` says which happened, so the UI never
 *  calls an existing run — possibly someone else's, already validated — "created". */
export interface CreateRunResult {
  run: PayrollRun;
  created: boolean;
}

/** Why a run was cancelled. Codes, not prose — see the payrollrun domain. */
export type RunCancelReason =
  | 'mistake' | 'wrong_period' | 'wrong_entity' | 'superseded' | 'not_needed' | 'other';

/** Ordered as the server lists them: 'mistake' first because it is the
 *  commonest honest reason, and when it is not offered people pick something
 *  inaccurate and every other count becomes untrustworthy. */
export const RUN_CANCEL_REASONS: { code: RunCancelReason; label: string }[] = [
  { code: 'mistake', label: 'Created by mistake' },
  { code: 'wrong_period', label: 'Wrong pay period' },
  { code: 'wrong_entity', label: 'Wrong legal entity' },
  { code: 'superseded', label: 'Replaced by another run' },
  { code: 'not_needed', label: 'No longer needed' },
  { code: 'other', label: 'Other' },
];

/** Mirrors payrollrun.ExternalPaymentMethods (Go), in the same order. */
export type RunExternalPaymentMethod =
  | 'bank_file' | 'bank_transfer' | 'cash' | 'mobile_money' | 'other';

/** The method records WHERE THE MONEY WENT — the rail — not which file was used
 *  to instruct it. Bavic (2026-09-28) took the generic "Bank disbursement" review
 *  file to the M-Pesa bulk portal: the file said bank, the money went by mobile
 *  money. Answering "how are customers actually paying?" needs the rail; which
 *  file they used belongs in the reference.
 *
 *  'bank_file' is first because it is the commonest route. 'other' is last and is
 *  the only one the server makes you explain — a method code nobody can interpret
 *  answers nothing. */
export const RUN_EXTERNAL_PAYMENT_METHODS: {
  code: RunExternalPaymentMethod;
  label: string;
  hint?: string;
}[] = [
  { code: 'bank_file', label: 'Bank', hint: 'a file or instruction submitted to your bank' },
  { code: 'bank_transfer', label: 'Bank transfer', hint: 'transfers made one by one in online banking' },
  { code: 'cash', label: 'Cash' },
  { code: 'mobile_money', label: 'Mobile money', hint: 'a bulk upload at M-Pesa, Mixx by Yas or Airtel Money' },
  { code: 'other', label: 'Other', hint: 'needs a reference or a short note saying how' },
];


// --- Pre-Payroll Control Center -------------------------------------------

export type AlertSeverity = 'low' | 'medium' | 'high' | 'critical';
export type AlertCategory = 'compliance' | 'fraud' | 'attendance' | 'payroll' | 'data_quality';
export type AlertStatus = 'open' | 'acknowledged' | 'resolved' | 'overridden';
export type GateStatus = 'ready' | 'warning' | 'blocked';

/** One pre-payroll risk/compliance alert raised against a run (or an employee in it). */
export interface RiskAlert {
  id: string;
  employeeId?: string;
  ruleCode: string;
  severity: AlertSeverity;
  category: AlertCategory;
  message: string;
  recommendedAction?: string;
  status: AlertStatus;
  overrideReason?: string;
  /** Optional deep-link for the operator to resolve the alert (e.g. a review page). */
  actionLink?: string;
  createdAt: string;
}

/** Statutory totals for the run — only the keys the country pack produces are present. */
export interface StatutoryTotals {
  paye?: number;
  nssfEmployee?: number;
  nssfEmployer?: number;
  sdl?: number;
  wcf?: number;
  heslb?: number;
}

/** Attendance signals — absent for tenants without scanner/attendance data. */
export interface AttendanceSignals {
  hasData: boolean;
  unapprovedOvertime: number;
  openExceptions: number;
  openCriticalExceptions: number;
  paidWithZeroAttendance: number;
}

/** Aggregated pre-payroll checks for one run (Control Center tab payload).
 *  Fetching it re-evaluates alerts server-side, so a refresh is just a refetch. */
export interface ControlCenter {
  runId: string;
  state: RunState;
  currency: Currency;
  population: { total: number; calculated: number; errors: number; excluded: number };
  /** Absent before the run has been validated/calculated. */
  totals?: { gross: number; net: number; employerCost: number };
  statutoryTotals: StatutoryTotals;
  changes: { salaryChanges: number; newHires: number };
  attendance?: AttendanceSignals;
  loans: { activeCount: number; expectedDeductions: number };
  readiness: { total: number; ready: number; warning: number; blocked: number; excluded: number };
  alerts: RiskAlert[];
  /** 0 (clean) – 100 (riskiest). */
  riskScore: number;
  gate: { status: GateStatus; blocking: string[] };
  /** ME7: people who are BOTH an employee and an independent contractor of
   *  this run's company — a HIGH, non-blocking "Confirm tax treatment"
   *  warning, confirmed on the person's engagements (never from this tab).
   *  Absent when the backend doesn't report it. */
  taxTreatment?: TaxTreatmentSection;
}

/** ME7 run-review section. `unavailable` = the check could not run — never "none". */
export interface TaxTreatmentSection {
  status: 'ok' | 'unavailable';
  findings: TaxTreatmentFinding[];
  errors: string[];
}

export interface TaxTreatmentFinding {
  personId: string;
  fullName?: string;
  legalEntityId: string;
  employmentEngagementId: string;
  contractorEngagementId: string;
  generation: string;
  provisional: boolean;
  overlapFrom?: string;
  overlapTo?: string;
}

// --- Statutory Filings register ---------------------------------------------

/** Statutory return kinds the filing register tracks (TZ country pack). */
export type FilingType = 'tra_paye' | 'sdl' | 'nssf' | 'wcf' | 'heslb' | 'bank_payment';

/** Filing lifecycle: generated → approved → submitted → accepted/rejected →
 *  completed (payment recorded). `amended` = superseded by a newer version. */
export type FilingStatus =
  | 'generated'
  | 'approved'
  | 'submitted'
  | 'accepted'
  | 'rejected'
  | 'completed'
  | 'amended';

/** One validation finding raised while generating the filing file. */
export interface FilingIssue {
  level: 'error' | 'warning';
  code: string;
  message: string;
}

/** Proof-of-filing/payment attachment (receipt, acknowledgment, screenshot…). */
export interface FilingEvidence {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  note?: string;
  uploadedBy?: string;
  uploadedAt: string;
}

/** Amount due on a filing obligation, in the company's own currency. */
export interface FilingAmount {
  /** Decimal string from the server (e.g. "1250000.0000"). */
  amount: string;
  currency: string;
}

/** `not_calculated` = no approved run covers the period yet. It carries no
 *  amount and must never render as 0. */
export type FilingAmountStatus = 'calculated' | 'not_calculated';

/** One statutory filing (a versioned register entry backed by a locked run). */
export interface Filing {
  id: string;
  legalEntityId: string;
  /** Enriched client-side from the legal-entities lookup. */
  legalEntityName: string;
  filingType: FilingType;
  period: string; // "2026-06"
  periodYear: number;
  periodMonth: number;
  version: number;
  status: FilingStatus;
  runId: string;
  exportId: string;
  validation: FilingIssue[];
  dueDate?: string;
  overdue: boolean;
  approvedBy?: string;
  approvedAt?: string;
  submittedBy?: string;
  submittedAt?: string;
  acknowledgmentRef?: string;
  controlNumber?: string;
  paymentReference?: string;
  rejectionReason?: string;
  /** Set on amended filings — the id of the version that replaced this one. */
  supersededBy?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  /** Only present on the detail endpoint. */
  evidence?: FilingEvidence[];
  /** Amount due from the run the filing was generated from (null when not calculated). */
  amountDue: FilingAmount | null;
  amountStatus: FilingAmountStatus;
}

/** A filing that is due for the period but hasn't been generated yet. */
export interface OutstandingFiling {
  legalEntityId: string;
  legalEntityName: string;
  filingType: FilingType;
  period: string;
  dueDate?: string;
  overdue: boolean;
  amountDue: FilingAmount | null;
  amountStatus: FilingAmountStatus;
}

/** Register counts, computed by the server over the companies the caller can see. */
export interface FilingCounts {
  outstanding: number;
  overdue: number;
  completed: number;
}

/** The register for one period: existing filings + what's still outstanding.
 *  Everything here is already bounded by the caller's company reach. */
export interface FilingRegister {
  period: string; // "2026-06" (server defaults to the previous month)
  filings: Filing[];
  outstanding: OutstandingFiling[];
  /** Server-side counts; null only from an older backend that did not send them. */
  counts: FilingCounts | null;
  /** Amount still due per currency (open obligations with a calculated amount). */
  totalsDue: FilingAmount[];
}

export interface NewFilingInput {
  legalEntityId: string;
  filingType: FilingType;
  year: number;
  month: number;
}

/** Authority decision on a submitted filing (reason required when rejected). */
export interface FilingOutcomeInput {
  result: 'accepted' | 'rejected';
  acknowledgmentRef?: string;
  controlNumber?: string;
  rejectionReason?: string;
}

/** Optional lock override (used after a 409 `risk_blocked` refusal). */
export interface LockRunOptions {
  override?: boolean;
  overrideReason?: string;
}

/** Aggregated view of one run's downstream artifacts (detail page payload). */
export interface RunDetail {
  run: PayrollRun;
  employees: EmployeeLine[];
  /** Set when the run's Employees read FAILED (backend message). `employees`
   *  is then empty but means "unknown", never "nobody": no headcount, empty
   *  state, Discard or picker may be derived from it. */
  employeesError?: string;
  approvals: Approval[];
  exports: PayrollExport[];
  batch: DisbursementBatch | null;
}
